// PRFAQSection.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const PRFAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'How do Express Entry and the Provincial Nominee Programs differ?',
      answer: 'Express Entry is federal and quick, with applications decided in 6 months, whereas a PNP is run by a single province and takes 18-24 months. Ranking under Express Entry is done on CRS points; each province instead sets its own criteria. The two connect at one important point: a provincial nomination is worth 600 CRS points, which in practice all but assures an invitation to apply through Express Entry.'
    },
    {
      question: 'Is a job offer necessary?',
      answer: 'In most cases, no. Neither the Federal Skilled Worker programme nor the Canadian Experience Class asks for one, and family sponsorship does not either. Certain PNP streams do require a job offer and others leave it optional. Where you do hold a valid offer, it lifts your CRS score considerably.'
    },
    {
      question: 'What goes into a CRS score?',
      answer: 'The Comprehensive Ranking System is how Express Entry candidates are ranked against one another. Points come from your age, with the peak falling between 20 and 29, along with your education, your English or French ability and your work history. Further points are available for things such as Canadian study or work experience. The ceiling is 1,200 points in all: 600 from core factors and another 600 from additional factors, provincial nomination among them.'
    },
    {
      question: 'Can my partner and children be included?',
      answer: 'Yes. A spouse or common-law partner and any dependent children can go on the same application and will be granted permanent resident status alongside you. Where your partner has strong language results, good qualifications or Canadian work experience, including them can add as much as 40 points to your CRS total. Each dependent child carries an extra government fee of CAD $230.'
    },
    {
      question: 'Which language tests count?',
      answer: 'English is tested through IELTS General Training or CELPIP General, French through TEF Canada or TCF Canada. Whichever you sit, the result has to be under 2 years old when you apply. Express Entry requires CLB 7 at minimum, roughly IELTS 6.0, and every band above that meaningfully raises your CRS score and your odds of an invitation.'
    },
    {
      question: 'Is an Educational Credential Assessment (ECA) required?',
      answer: 'If you studied outside Canada and are going through Express Entry or most PNP streams, yes. The assessment confirms that your degree, diploma or certificate is genuine and equivalent to a Canadian one. WES, IQAS and ICES are among the designated organisations able to issue it, and turnaround usually falls between 4 and 8 weeks.'
    },
    {
      question: 'What comes next once PR is granted?',
      answer: 'Your Confirmation of Permanent Residence carries an expiry date, and you have to land in Canada before it passes. Landing triggers the issue of your PR card, valid for 5 years. From then on you can live, work or study in any province, use the healthcare system, and eventually apply for citizenship once the residency requirement is met, normally 3 years inside a 5-year window.'
    },
    {
      question: 'Does permanent residency run out?',
      answer: 'The status itself does not expire, though the card does after 5 years and can be renewed before then. What matters is presence: you have to spend at least 730 days, meaning 2 years, physically in Canada during any 5-year period. Reach 3 years of physical presence and Canadian citizenship becomes an option.'
    },
    {
      question: 'Am I allowed to work while the application is pending?',
      answer: 'Anyone already in Canada on a valid work permit can keep working throughout. Applicants still overseas cannot work until PR is granted and they have landed. If your current permit is due to run out mid-application, a bridging open work permit may be available to cover the gap.'
    },
    {
      question: 'How often do PR applications succeed?',
      answer: 'It depends heavily on the programme and on individual circumstances. Complete Express Entry applications that meet every requirement are approved at a rate of over 80%. We do not publish a success rate of our own. What decides an outcome is nearly always the same three things: genuine eligibility, complete documentation, and meeting each requirement the programme sets.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-base font-light">
            The questions we are asked most often about applying for PR in Canada
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-surface rounded-lg shadow-sm overflow-hidden border border-gray-200"
            >
              {/* Question */}
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-base font-semibold text-primary pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={24} className="text-secondary" />
                  ) : (
                    <Plus size={24} className="text-secondary" />
                  )}
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 text-sm font-light leading-relaxed border-t border-gray-200 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Additional Help */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Something not covered here?</strong> Book a free consultation and one of our advisers will look at your circumstances and set out what your options actually are.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PRFAQSection;
